import React from 'react';
import styled from 'styled-components';
import { Colors } from '../../utils/colors';
import { Sizes } from '../../utils/sizes';

const PageContainer = styled.section`
  width: 100%;
  min-height: 100vh;
  background: linear-gradient(135deg, ${Colors.background} 0%, ${Colors.surface} 100%);
  padding: 60px 20px 80px;

  @media (max-width: ${Sizes.mediaQueryBreak1}) {
    padding: 40px 16px 60px;
  }
`;

const Wrapper = styled.div`
  max-width: 860px;
  margin: 0 auto;
`;

const DocCard = styled.div`
  background: rgba(18, 18, 18, 0.7);
  border: 1px solid rgba(255, 215, 0, 0.25);
  border-radius: 14px;
  padding: 52px 48px;
  color: ${Colors.white};

  @media (max-width: ${Sizes.mediaQueryBreak1}) {
    padding: 32px 24px;
    border-radius: 10px;
  }

  @media (max-width: 480px) {
    padding: 24px 16px;
  }
`;

const DocHeader = styled.header`
  border-bottom: 1px solid rgba(255, 215, 0, 0.15);
  padding-bottom: 28px;
  margin-bottom: 36px;

  @media (max-width: ${Sizes.mediaQueryBreak1}) {
    padding-bottom: 20px;
    margin-bottom: 28px;
  }

  & .brand {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 20px;

    & .logo-mark {
      width: 36px;
      height: 36px;
      background: ${Colors.yellow};
      border-radius: 8px;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 18px;
      flex-shrink: 0;
    }

    & .brand-name {
      color: ${Colors.yellow};
      font-size: 15px;
      font-weight: 800;
      letter-spacing: 0.3px;
      text-transform: uppercase;
    }
  }

  & h1 {
    color: ${Colors.yellow};
    font-size: 28px;
    font-weight: 900;
    margin: 0 0 8px 0;
    line-height: 1.2;
    letter-spacing: -0.3px;

    @media (max-width: ${Sizes.mediaQueryBreak1}) {
      font-size: 22px;
    }
  }

  & .last-update {
    font-size: 12px;
    color: rgba(255, 215, 0, 0.5);
    font-weight: 500;
  }
`;

const Section = styled.section`
  margin-bottom: 32px;

  @media (max-width: ${Sizes.mediaQueryBreak1}) {
    margin-bottom: 24px;
  }

  &:last-of-type {
    margin-bottom: 0;
  }
`;

const SectionTitle = styled.h2`
  font-size: 14px;
  font-weight: 900;
  color: ${Colors.yellow};
  text-transform: uppercase;
  letter-spacing: 0.6px;
  margin: 0 0 12px 0;
  display: flex;
  align-items: center;
  gap: 10px;

  &::before {
    content: '';
    display: inline-block;
    width: 3px;
    height: 14px;
    background: ${Colors.yellow};
    border-radius: 2px;
    flex-shrink: 0;
  }

  @media (max-width: 480px) {
    font-size: 12px;
  }
`;

const SubTitle = styled.h3`
  font-size: 13px;
  font-weight: 700;
  color: rgba(255, 215, 0, 0.75);
  margin: 18px 0 8px 0;
  letter-spacing: 0.2px;
`;

const Paragraph = styled.p`
  font-size: 14px;
  line-height: 1.75;
  color: rgba(255, 255, 255, 0.82);
  margin: 0 0 10px 0;

  &:last-child {
    margin-bottom: 0;
  }

  @media (max-width: 480px) {
    font-size: 13px;
  }
`;

const List = styled.ul`
  margin: 8px 0 10px 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 6px;

  & li {
    font-size: 14px;
    line-height: 1.6;
    color: rgba(255, 255, 255, 0.8);
    display: flex;
    align-items: flex-start;
    gap: 8px;

    &::before {
      content: '·';
      color: ${Colors.yellow};
      font-weight: 900;
      font-size: 18px;
      line-height: 1.3;
      flex-shrink: 0;
    }

    @media (max-width: 480px) {
      font-size: 13px;
    }
  }
`;

const Highlight = styled.div`
  background: rgba(255, 215, 0, 0.05);
  border-left: 3px solid rgba(255, 215, 0, 0.5);
  border-radius: 0 6px 6px 0;
  padding: 12px 16px;
  margin: 10px 0;

  & p {
    font-size: 14px;
    color: rgba(255, 255, 255, 0.85);
    line-height: 1.65;
    margin: 0;
  }

  & a {
    color: ${Colors.yellow};
    text-decoration: none;
    font-weight: 600;

    &:hover {
      text-decoration: underline;
    }
  }
`;

const Divider = styled.hr`
  border: none;
  border-top: 1px solid rgba(255, 215, 0, 0.1);
  margin: 28px 0;
`;

const TermosDeUso = () => {
  return (
    <PageContainer>
      <Wrapper>
        <DocCard>
          <DocHeader>
            <div className="brand">
              <div className="logo-mark">🎸</div>
              <span className="brand-name">Violão Católico</span>
            </div>
            <h1>Termos de Uso e Política de Privacidade</h1>
            <span className="last-update">Última atualização: 13 de maio de 2026</span>
          </DocHeader>

          <Section>
            <SectionTitle>1. Sobre o Curso</SectionTitle>
            <Paragraph>
              O presente documento estabelece os Termos de Uso e a Política de Privacidade aplicáveis ao curso de Violão Fingerstyle disponibilizado pela plataforma Violão Católico.
            </Paragraph>
            <Paragraph>
              A empresa responsável pelo curso é <strong>MUSITECH STUDIO DIGITAL LTDA</strong>, inscrita no CNPJ nº 40.126.747/0001-05.
            </Paragraph>
            <Paragraph>
              Ao realizar cadastro, compra ou acessar o curso, o usuário declara estar de acordo com os termos descritos neste documento.
            </Paragraph>
          </Section>

          <Divider />

          <Section>
            <SectionTitle>2. Modalidade de Compra e Acesso</SectionTitle>
            <Paragraph>
              O curso é comercializado em modalidade de <strong>pagamento único</strong>, com acesso vitalício ao conteúdo disponibilizado na plataforma.
            </Paragraph>
            <Paragraph>
              O valor integral do curso é de <strong>R$ 297,00</strong> (duzentos e noventa e sete reais), podendo haver descontos promocionais aplicados temporariamente.
            </Paragraph>
            <Paragraph>
              O acesso ao curso é pessoal e intransferível.
            </Paragraph>
          </Section>

          <Divider />

          <Section>
            <SectionTitle>3. Cadastro do Usuário</SectionTitle>
            <Paragraph>Para acessar o curso, o usuário deverá criar uma conta informando:</Paragraph>
            <List>
              <li>Nome</li>
              <li>E-mail</li>
            </List>
            <Paragraph>
              Durante o processo de pagamento, poderá ser solicitado o CPF do usuário exclusivamente por exigência do sistema bancário e dos intermediadores de pagamento.
            </Paragraph>
            <Paragraph>
              A plataforma não realiza armazenamento de CPF em sua base de dados própria.
            </Paragraph>
          </Section>

          <Divider />

          <Section>
            <SectionTitle>4. Dados Coletados</SectionTitle>
            <Paragraph>Os dados armazenados pela plataforma poderão incluir:</Paragraph>
            <List>
              <li>Nome</li>
              <li>E-mail</li>
              <li>Histórico de acesso ao curso</li>
              <li>Informações relacionadas à compra</li>
            </List>
            <Paragraph>Esses dados são utilizados exclusivamente para:</Paragraph>
            <List>
              <li>Liberação de acesso</li>
              <li>Suporte ao usuário</li>
              <li>Comunicação sobre o curso</li>
              <li>Recuperação de conta</li>
              <li>Cumprimento de obrigações legais e financeiras</li>
            </List>
          </Section>

          <Divider />

          <Section>
            <SectionTitle>5. Privacidade e Segurança</SectionTitle>
            <Paragraph>
              A plataforma adota medidas técnicas e administrativas adequadas para proteção dos dados dos usuários, buscando prevenir acessos não autorizados, vazamentos, alterações indevidas ou uso inadequado das informações.
            </Paragraph>
            <Paragraph>
              Os dados dos usuários não são vendidos, alugados ou compartilhados com terceiros para fins comerciais indevidos.
            </Paragraph>
            <Paragraph>
              Informações de pagamento são processadas por plataformas financeiras terceirizadas responsáveis pela operação bancária.
            </Paragraph>
          </Section>

          <Divider />

          <Section>
            <SectionTitle>6. Direitos Autorais</SectionTitle>

            <SubTitle>6.1 Materiais do Curso</SubTitle>
            <Paragraph>Todo o conteúdo disponibilizado no curso, incluindo:</Paragraph>
            <List>
              <li>Aulas e vídeos</li>
              <li>Materiais complementares</li>
              <li>Cifras e arranjos</li>
              <li>Imagens e identidade visual</li>
            </List>
            <Paragraph>é protegido por direitos autorais. É proibido:</Paragraph>
            <List>
              <li>Copiar ou redistribuir</li>
              <li>Revender ou compartilhar acesso</li>
              <li>Gravar ou disponibilizar o conteúdo publicamente sem autorização expressa</li>
            </List>
            <Paragraph>
              O descumprimento poderá resultar em suspensão definitiva do acesso e medidas legais cabíveis.
            </Paragraph>

            <SubTitle>6.2 E-books e Compilações de Cifras</SubTitle>
            <Paragraph>
              Os e-books disponibilizados pela plataforma foram organizados, revisados, corrigidos e estruturados pela equipe responsável pelo projeto Violão Católico.
            </Paragraph>
            <Paragraph>
              As cifras presentes nesses materiais são conteúdos amplamente públicos e facilmente encontrados na internet em diferentes plataformas e fontes. O diferencial do material disponibilizado está na curadoria, organização, padronização, correção e estruturação realizada manualmente para facilitar o aprendizado dos usuários.
            </Paragraph>
            <Paragraph>
              A plataforma não reivindica autoria ou direitos autorais exclusivos sobre as cifras das músicas presentes nos e-books. Além disso, as músicas presentes nos materiais digitais já possuem videoaulas publicadas no canal oficial Violão Católico.
            </Paragraph>
          </Section>

          <Divider />

          <Section>
            <SectionTitle>7. Usuários de Curso Anterior</SectionTitle>
            <Paragraph>
              Usuários que adquiriram anteriormente versões anteriores do curso de fingerstyle vinculadas à plataforma ou projeto Violão Católico terão acesso irrestrito ao novo curso após comprovação válida da compra anterior.
            </Paragraph>
            <Paragraph>
              A liberação será realizada mediante análise da equipe responsável.
            </Paragraph>
          </Section>

          <Divider />

          <Section>
            <SectionTitle>8. Reembolso</SectionTitle>

            <SubTitle>8.1 Curso de Violão Fingerstyle</SubTitle>
            <Paragraph>
              O usuário poderá solicitar reembolso integral no prazo de até <strong>7 (sete) dias corridos</strong> após a compra, conforme previsto no Código de Defesa do Consumidor brasileiro para compras realizadas pela internet.
            </Paragraph>
            <Paragraph>
              Após esse período, não haverá obrigação legal de reembolso, salvo situações excepcionais analisadas individualmente.
            </Paragraph>
            <Highlight>
              <p>Solicitações deverão ser enviadas para: <a href="mailto:contato@violaocatolico.com">contato@violaocatolico.com</a></p>
            </Highlight>

            <SubTitle>8.2 Produtos Digitais para Download (E-books e Materiais Digitais)</SubTitle>
            <Paragraph>
              Ao realizar a compra de produtos digitais disponibilizados pela plataforma, incluindo e-books e materiais para download, o usuário declara estar ciente de que o acesso ao conteúdo é liberado imediatamente após a confirmação do pagamento, incluindo possibilidade de visualização e download do material adquirido.
            </Paragraph>
            <Paragraph>
              Por se tratar de conteúdo digital de consumo imediato, <strong>não será possível solicitar reembolso após o acesso, visualização ou download do produto</strong>.
            </Paragraph>
            <Paragraph>
              Ao concluir a compra, o usuário concorda expressamente com estas condições.
            </Paragraph>
          </Section>

          <Divider />

          <Section>
            <SectionTitle>9. Suspensão de Conta</SectionTitle>
            <Paragraph>A plataforma poderá suspender ou cancelar o acesso de usuários que:</Paragraph>
            <List>
              <li>Pratiquem fraude</li>
              <li>Compartilhem contas</li>
              <li>Violem direitos autorais</li>
              <li>Utilizem a plataforma de forma abusiva</li>
              <li>Descumpram estes termos</li>
            </List>
          </Section>

          <Divider />

          <Section>
            <SectionTitle>10. Alterações do Conteúdo e Plataforma</SectionTitle>
            <Paragraph>O curso poderá receber:</Paragraph>
            <List>
              <li>Atualizações e novos módulos</li>
              <li>Melhorias técnicas</li>
              <li>Alterações estruturais</li>
            </List>
            <Paragraph>
              Isso não caracteriza criação de nova obrigação contratual além do acesso vitalício ao conteúdo disponibilizado pela plataforma.
            </Paragraph>
          </Section>

          <Divider />

          <Section>
            <SectionTitle>11. Direitos do Usuário Sobre os Dados</SectionTitle>
            <Paragraph>O usuário poderá solicitar:</Paragraph>
            <List>
              <li>Acesso aos seus dados</li>
              <li>Correção de informações</li>
              <li>Exclusão da conta</li>
              <li>Remoção de dados armazenados, quando legalmente possível</li>
            </List>
            <Highlight>
              <p>As solicitações deverão ser feitas pelo e-mail: <a href="mailto:contato@violaocatolico.com">contato@violaocatolico.com</a></p>
            </Highlight>
          </Section>

          <Divider />

          <Section>
            <SectionTitle>12. Contato</SectionTitle>
            <Highlight>
              <p>Dúvidas, suporte ou solicitações relacionadas a este documento poderão ser realizadas através do e-mail: <a href="mailto:contato@violaocatolico.com">contato@violaocatolico.com</a></p>
            </Highlight>
          </Section>

          <Divider />

          <Section>
            <SectionTitle>13. Foro</SectionTitle>
            <Paragraph>
              Fica eleito o foro da Comarca de <strong>Montes Claros/MG</strong> para resolução de eventuais conflitos relacionados a este documento, com aplicação da legislação brasileira.
            </Paragraph>
          </Section>
        </DocCard>
      </Wrapper>
    </PageContainer>
  );
};

export default TermosDeUso;
